import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { calibrationApi } from '@/api/calibration.api';
import { useCan } from '@/hooks/useCan';

export const INVENTORY_SUGGESTIONS_KEY = ['inventory-suggestions'] as const;

/**
 * Pending usage suggestions (procedures-per-unit calibration).
 *
 * One query, shared by the nav badge and the Stock Levels page, so the count
 * in the sidebar and the entry point on the page can never disagree. The
 * analysis itself runs nightly on the server; this only reads its output.
 */
export function useInventorySuggestions() {
  const { canView } = useCan();
  const enabled = canView('inventory');

  const query = useQuery({
    queryKey: INVENTORY_SUGGESTIONS_KEY,
    queryFn: () => calibrationApi.getPendingRecommendations(),
    enabled,
    staleTime: 10 * 60_000,
  });

  return {
    suggestions: query.data ?? [],
    count: query.data?.length ?? 0,
    isLoading: query.isLoading,
  };
}

export function useInventorySuggestionActions() {
  const qc = useQueryClient();
  const refresh = () =>
    qc.invalidateQueries({ queryKey: INVENTORY_SUGGESTIONS_KEY });

  const accept = useMutation({
    mutationFn: (id: string) => calibrationApi.acceptRecommendation(id),
    onSuccess: refresh,
  });
  const dismiss = useMutation({
    mutationFn: (id: string) => calibrationApi.dismissRecommendation(id),
    onSuccess: refresh,
  });
  const rerun = useMutation({
    mutationFn: () => calibrationApi.analyzeAndGenerate(),
    onSuccess: refresh,
  });

  return { accept, dismiss, rerun };
}
